import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader, Text, Paper, Pagination, Select, Group } from "@mantine/core";
import { Alert } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import { useMonitoring } from "@hooks/useMonitoring";
import type {
    MonitoringProcessViewModel,
    MonitoringMappingJobViewModel,
} from "@models/monitoring";
import { DateRangeFilter } from "@features/index";
import {
    ExtractionProcessTable,
    MappingJobTable,
    SearchUsageTable,
} from "@features/Monitoring";
import type { CostFilterParams } from "@models/cost";
import dayjs from "dayjs";
import "./MonitoringPage.scss";

type ServiceFilter = "extraction" | "mapping" | "search";

export const MonitoringPage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const {
        monitoringProcesses,
        mappingJobs,
        searchUsage,
        total,
        isLoading,
        error,
        fetchMonitoringProcesses,
        fetchMappingJobs,
        fetchSearchUsage,
    } = useMonitoring();

    const [service, setService] = useState<ServiceFilter>("extraction");
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filters, setFilters] = useState<CostFilterParams>(() => ({
        startDate: dayjs().subtract(7, "day").format("YYYY-MM-DD"),
        endDate: dayjs().format("YYYY-MM-DD"),
    }));

    useEffect(() => {
        const fetchFilters = {
            limit: rowsPerPage,
            offset: (page - 1) * rowsPerPage,
            startDate: filters.startDate,
            endDate: filters.endDate,
        };

        if (service === "mapping") {
            fetchMappingJobs(fetchFilters);
        } else if (service === "search") {
            fetchSearchUsage(fetchFilters);
        } else {
            // extraction — the PROCESS# cost-usage list.
            fetchMonitoringProcesses(fetchFilters);
        }
    }, [
        service,
        page,
        rowsPerPage,
        filters.startDate,
        filters.endDate,
        fetchMonitoringProcesses,
        fetchMappingJobs,
        fetchSearchUsage,
    ]);

    const handleFilterChange = (newFilters: CostFilterParams) => {
        setFilters((prev) => ({ ...prev, ...newFilters }));
        setPage(1);
    };

    const handleServiceChange = (value: string | null) => {
        if (!value) return;
        setService(value as ServiceFilter);
        setPage(1);
    };

    const handleChangePage = (newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (value: string | null) => {
        if (value) {
            setRowsPerPage(parseInt(value, 10));
            setPage(1);
        }
    };

    const handleProcessRowClick = (process: MonitoringProcessViewModel) => {
        navigate(`/admin/monitoring/process/${process.id}`, {
            state: { createdBy: process.createdBy, organisationName: process.organisationName, doctypeName: process.doctypeName },
        });
    };

    const handleMappingRowClick = (job: MonitoringMappingJobViewModel) => {
        navigate(`/admin/monitoring/mapping-job/${job.jobId}`, {
            state: { createdBy: job.createdBy, organisationName: job.organisationName },
        });
    };

    const totalPages = Math.ceil(total / rowsPerPage);

    const rowCount =
        service === "mapping"
            ? mappingJobs.length
            : service === "search"
              ? searchUsage.length
              : monitoringProcesses.length;

    const emptyStateKey =
        service === "mapping"
            ? "admin.monitoring.empty.noMappingJobs"
            : service === "search"
              ? "admin.monitoring.empty.noSearchUsage"
              : "admin.monitoring.empty.noProcesses";

    const renderTable = () => {
        if (service === "mapping") {
            return (
                <MappingJobTable
                    jobs={mappingJobs}
                    onRowClick={handleMappingRowClick}
                />
            );
        }
        if (service === "search") {
            return <SearchUsageTable entries={searchUsage} />;
        }
        return (
            <ExtractionProcessTable
                processes={monitoringProcesses}
                onRowClick={handleProcessRowClick}
            />
        );
    };

    return (
        <div className="monitoring-page">
            <div className="monitoring-page__header">
                <Text
                    size="lg"
                    fw={600}
                    component="h1"
                    className="monitoring-page__title"
                >
                    {t("admin.monitoring.title")}
                </Text>
                <Group gap="sm" align="flex-end" className="monitoring-page__controls">
                    <DateRangeFilter
                        onFilterChange={handleFilterChange}
                        initialFilters={filters}
                    />
                    <Select
                        label={t("admin.monitoring.serviceFilter.label")}
                        value={service}
                        onChange={handleServiceChange}
                        allowDeselect={false}
                        size="sm"
                        w={160}
                        data={[
                            { value: "extraction", label: t("admin.monitoring.serviceFilter.extraction") },
                            { value: "mapping", label: t("admin.monitoring.serviceFilter.mapping") },
                            { value: "search", label: t("admin.monitoring.serviceFilter.search") },
                        ]}
                        className="monitoring-page__service-select"
                    />
                </Group>
            </div>

            {error && (
                <Alert color="red" mb="sm">
                    {error}
                </Alert>
            )}
            {isLoading ? (
                <div className="monitoring-page__loading">
                    <Loader />
                </div>
            ) : rowCount === 0 ? (
                <Text c="dimmed" ta="center" py="xl">{t(emptyStateKey)}</Text>
            ) : (
                <Paper className="monitoring-page__table-container">
                    {renderTable()}
                    {service !== "search" && (
                        <Group
                            justify="space-between"
                            align="center"
                            p="sm"
                            className="monitoring-page__pagination"
                        >
                            <Group gap="xs" align="center">
                                <Text size="sm">{t("admin.monitoring.pagination.rowsPerPage")}</Text>
                                <Select
                                    data={[
                                        { value: "10", label: "10" },
                                        { value: "20", label: "20" },
                                        { value: "50", label: "50" },
                                    ]}
                                    value={String(rowsPerPage)}
                                    onChange={handleChangeRowsPerPage}
                                    size="xs"
                                    style={{ width: 70 }}
                                />
                            </Group>
                            <Pagination
                                total={totalPages}
                                value={page}
                                onChange={handleChangePage}
                                size="sm"
                            />
                        </Group>
                    )}
                </Paper>
            )}
        </div>
    );
};
